import { type FC } from "react"

import {
	footerSocialPlatformLabels,
	hasLandingPageNavigation,
	type LandingPageFooterBlock,
	type LandingPageNavigationPage,
} from "@forento/core/models/landing"
import FooterSocialIcon from "@forento/ui/components/FooterSocialIcon"
import { cn } from "@forento/ui/utilities/tailwind"

type Props = {
	block: LandingPageFooterBlock
	navigationPages: LandingPageNavigationPage[]
	currentPath: string
}
const FooterBlockPreview: FC<Props> = ({ block, navigationPages, currentPath }) => {
	const { companyName, address, email, socialLinks } = block.content
	const activeSocialLinks = (socialLinks ?? []).filter(link => link.url.trim().length > 0)
	const showNavigation = hasLandingPageNavigation(navigationPages)

	return (
		<footer className="bg-landing-footer mt-16 px-6 py-12 sm:mt-20 lg:px-8">
			<div className="mx-auto max-w-7xl">
				<div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
					<div className="flex flex-col gap-2">
						{companyName && (
							<p className="text-landing-footer-foreground text-lg font-semibold">{companyName}</p>
						)}
						{address && (
							<p className="text-landing-footer-foreground-muted text-sm whitespace-pre-line">
								{address}
							</p>
						)}
						{email && <span className="text-landing-footer-foreground-muted text-sm">{email}</span>}
					</div>

					<div className="flex flex-col items-start gap-6 md:items-end">
						{showNavigation && (
							<nav
								aria-label="Footer navigation"
								className="flex flex-wrap gap-x-5 gap-y-2 md:justify-end"
							>
								{navigationPages.map(page => {
									const isActive = page.path === currentPath
									return (
										<span
											key={page.id}
											className={cn(
												isActive
													? "text-landing-footer-foreground text-sm font-semibold"
													: "text-landing-footer-foreground-muted text-sm",
											)}
										>
											{page.title ?? page.path}
										</span>
									)
								})}
							</nav>
						)}

						{activeSocialLinks.length > 0 && (
							<div className="flex items-center gap-4">
								{activeSocialLinks.map(link => (
									<span
										key={link.platform}
										role="img"
										aria-label={footerSocialPlatformLabels[link.platform]}
										className="text-landing-footer-foreground-muted"
									>
										<FooterSocialIcon platform={link.platform} className="size-6" />
									</span>
								))}
							</div>
						)}
					</div>
				</div>

				<div className="border-landing-footer-foreground/10 mt-8 flex flex-col gap-2 border-t pt-8 sm:flex-row sm:items-center sm:justify-between">
					{companyName && (
						<p className="text-landing-footer-foreground-muted/80 text-xs">
							&copy; {new Date().getFullYear()} {companyName}. All rights reserved.
						</p>
					)}
					<nav aria-label="Footer policy links" className="flex flex-wrap gap-x-4 gap-y-2">
						<span className="text-landing-footer-foreground-muted/80 text-xs">Terms of Service</span>
						<span className="text-landing-footer-foreground-muted/80 text-xs">Privacy Policy</span>
					</nav>
				</div>
			</div>
		</footer>
	)
}

export default FooterBlockPreview
